async function ucEnforceLinkedPromoItems(source = 'unknown') {

  const cartRes = await fetch('/cart.js', {
    headers: { Accept: 'application/json' }
  });
  const cart = await cartRes.json();

  const items = Array.isArray(cart.items) ? cart.items : [];

  const parentByGroup = new Map();

  items.forEach((item) => {
    const props = item.properties || {};
    if (props._uc_bundle_role === 'parent' && props._uc_bundle_group) {
      parentByGroup.set(props._uc_bundle_group, item);
    }
  });

  const updates = {};

  items.forEach((item) => {
    const props = item.properties || {};
    if (props._uc_bundle_role !== 'child' || !props._uc_bundle_group) return;

    const parent = parentByGroup.get(props._uc_bundle_group);

    // 1) No parent -> remove child
    if (!parent) {
      updates[item.key] = 0;
      return;
    }

    // 2) Parent exists but qty differs -> sync child to parent qty
    if (Number(item.quantity) !== Number(parent.quantity)) {
      updates[item.key] = parent.quantity;
    }
  });

  if (Object.keys(updates).length) {
    const updateRes = await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ updates })
    });

    const updateText = await updateRes.text();

    if (!updateRes.ok) {
      throw new Error(updateText || 'cart/update.js failed');
    }

    return true;
  }

  return false;
}

async function ucEnforceNotOnlyVariantRule(source = 'unknown') {
  const TARGET_VARIANT_ID = 52619193745687;

  const cartRes = await fetch('/cart.js', {
    headers: { Accept: 'application/json' }
  });
  const cart = await cartRes.json();

  const items = Array.isArray(cart.items) ? cart.items : [];
  const targetItem = items.find(
    (item) => Number(item.variant_id) === TARGET_VARIANT_ID
  );

  // Not present -> nothing to do
  if (!targetItem) return false;

  // If any other item exists, allow it
  const otherItems = items.filter(
    (item) => Number(item.variant_id) !== TARGET_VARIANT_ID
  );

  if (otherItems.length > 0) return false;

  // Target variant is alone -> remove it
  const updateRes = await fetch('/cart/update.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json'
    },
    body: JSON.stringify({
      updates: {
        [targetItem.key]: 0
      }
    })
  });

  const updateText = await updateRes.text();

  if (!updateRes.ok) {
    throw new Error(updateText || 'cart/update.js failed');
  }

  return true;
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    this.addEventListener('click', (event) => {
      // Redirect specific variant when clicked in cart
      const link = event.target.closest('a[href*="variant="]');
      if (link) {
        try {
          const url = new URL(link.href, window.location.origin);
          const variantId = Number(url.searchParams.get('variant') || 0);

          const REDIRECT_VARIANT_ID = 52619193745687;

          if (variantId === REDIRECT_VARIANT_ID) {
            event.preventDefault();
            window.location.href = '/';
            return;
          }
        } catch (err) {
          console.warn('[UC] Variant redirect check failed', err);
        }
      }

      // Listen for remove clicks inside the drawer
      const removeTrigger = event.target.closest(
        'cart-remove-button, .cart-remove-button, [name="remove"], a[href*="line="]'
      );

      if (!removeTrigger) return;

      // Wait for Dawn/Shopify to finish its own cart update first
      setTimeout(async () => {
        try {
          const changedLinked = await ucEnforceLinkedPromoItems('drawer remove click');
          const changedLonely = await ucEnforceNotOnlyVariantRule('drawer remove click');

          if (changedLinked || changedLonely) {
            window.location.reload();
          }
        } catch (err) {
          console.error('[UC PROMO] enforcement failed after remove click:', err);
        }
      }, 700);
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {

    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    // Clear empty-state classes on first real render
    this.classList.contains('is-empty') &&
      this.classList.remove('is-empty');

    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner && drawerInner.classList.contains('is-empty')) {
      drawerInner.classList.remove('is-empty');
    }

    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;

      const sectionHtml = parsedState?.sections?.[section.id];
      if (!sectionHtml) return;

      sectionElement.innerHTML = this.getSectionInnerHTML(sectionHtml, section.selector);
    });

    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) {
        overlay.addEventListener('click', this.close.bind(this));
      }
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

document.addEventListener('DOMContentLoaded', () => {
  Promise.all([
    ucEnforceLinkedPromoItems('DOMContentLoaded'),
    ucEnforceNotOnlyVariantRule('DOMContentLoaded')
  ])
    .then(([changedLinked, changedLonely]) => {
      if (changedLinked || changedLonely) {
        window.location.reload();
      }
    })
    .catch((err) => {
      console.error('[UC PROMO] DOMContentLoaded enforcement failed:', err);
    });
});